import useValidationCockpit from "./useValidationCockpit";
import { Checkbox } from "@/components/ui/checkbox";
import { CircleDashed, CheckCircle2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { InfoIcon } from "lucide-react";

const ValidationCockpit = ({setIsValidationCompleted}: {setIsValidationCompleted?: React.Dispatch<React.SetStateAction<boolean>>}) => {
  const {
    leftValidations,
    rightValidations,
    toggleValidation,
    progress,
    isLocked,
    isUpdateValidationPending,
    hasEditAccess
  } = useValidationCockpit({setIsValidationCompleted});

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">

            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              Validation Cockpit
            </h2>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger type="button">
                  <InfoIcon className="h-5 w-5 stroke-primary cursor-pointer" />
                </TooltipTrigger>

                <TooltipContent className="w-[420px] max-w-none p-4">
                  <div className="flex flex-col text-[13px] text-primary space-y-2 leading-relaxed">

                    <span>
                      The Financial Year Closing Cockpit is a mandatory step before
                      year-end transitions. The Carry Forward function unlocks only
                      after 100% cockpit completion to ensure data accuracy.
                    </span>

                    <span>
                      <b>System-Generated Tests</b> – Automated checks marked
                      “Pass” when criteria are met.
                    </span>

                    <span>
                      <b>Manual Verifications</b> – Users review records and
                      confirm completion manually. Carry Forward is enabled
                      only after all checks are completed.
                    </span>

                  </div>
                </TooltipContent>

              </Tooltip>
            </TooltipProvider>

          </div>

          <p className="text-sm text-gray-500 mt-1">
            Complete the pre-closing conditions. Status turns Amber only when all validations pass.
          </p>

        </div>

        <div className="w-full lg:w-[300px] flex flex-col gap-2">

          <div className="flex justify-end">
            <span className="text-sm font-medium text-gray-600">
              {progress}% Completed
            </span>
          </div>

          <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <div className="space-y-4">
          {leftValidations?.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-sm font-medium">
                  {item.title}
                </h3>
                {item.status === "Passed" ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm font-medium whitespace-nowrap">
                    <CheckCircle2 size={16} />
                    Passed
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-500 text-sm font-medium whitespace-nowrap">
                    <CircleDashed size={16} />
                    Pending
                  </div>
                )}
              </div>

              {item.description && (
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {rightValidations?.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-white"
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={item.checked}
                  disabled={isLocked || isUpdateValidationPending || !hasEditAccess}
                  onCheckedChange={() => {
                    if (!isLocked) toggleValidation(item.id);
                  }}
                  className="cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-800">
                  {item.title}
                </span>
              </div>
              {item.status === "Passed" ? (
                <div className="flex items-center gap-2 text-green-600 text-sm font-medium whitespace-nowrap">
                  <CheckCircle2 size={16} />
                  Passed
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-500 text-sm font-medium whitespace-nowrap">
                  <CircleDashed size={16} />
                  Pending
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ValidationCockpit;